import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { initialEntries } from './data/store';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import { apiGetEntries, apiCreateEntry, apiMe, clearToken, getToken } from './services/api';
import TimelineView from './components/timeline/TimelineView';
import LogView from './components/log/LogView';
import RoutinesView from './components/routines/RoutinesView';
import WishlistView from './components/wishlist/WishlistView';
import ProfileView from './components/profile/ProfileView';
import TimelapseView from './components/timelapse/TimelapseView';
import BottomNav from './components/nav/BottomNav';
import AuthScreen from './components/auth/AuthScreen';
import PricingView from './components/pricing/PricingView';
import LandingPage from './components/landing/LandingPage';
import ShopView from './components/shop/ShopView';
import PrivacyPage from './components/legal/PrivacyPage';
import TermsPage from './components/legal/TermsPage';

function getThisMonthCount(entries) {
  const now = new Date();
  return entries.filter((e) => {
    const d = new Date(e.date);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }).length;
}

function TrialBanner({ daysLeft, onOpenPricing }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex-shrink-0 z-20 relative"
      style={{ background: 'linear-gradient(90deg, #7c3aed, #a855f7)' }}
    >
      <button
        className="w-full px-4 py-2 flex items-center justify-between gap-2"
        onClick={onOpenPricing}
      >
        <span className="font-sans text-xs text-white font-medium">
          ✨ Radiant Pro trial — <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> left · Add a card to keep access
        </span>
        <span
          onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
          className="text-white/60 text-sm leading-none px-1 active:text-white"
        >×</span>
      </button>
    </motion.div>
  );
}

function AppShell() {
  const { canLog, isOnTrial, trialDaysLeft, startTrial, syncPlanFromServer } = useSubscription();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('timeline');
  // Start empty — initialEntries are only for demo mode, set explicitly in handleAuth
  const [entries, setEntries] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [checkoutBanner, setCheckoutBanner] = useState(null); // 'success' | null

  // On mount: if a token exists, re-validate it and load entries from the backend.
  // Also handles returning from Stripe Checkout (?checkout=success).
  useEffect(() => {
    if (!getToken()) return;

    const params = new URLSearchParams(window.location.search);
    const isCheckoutReturn = params.get('checkout') === 'success';
    if (isCheckoutReturn) {
      // Clean the URL without a page reload
      window.history.replaceState({}, '', window.location.pathname);
    }

    apiMe()
      .then(({ user: u }) => {
        setUser(u);
        syncPlanFromServer(u.plan); // always trust the server for plan status
        if (isCheckoutReturn) setCheckoutBanner('success');
        return apiGetEntries();
      })
      .then(serverEntries => {
        setEntries(Array.isArray(serverEntries) ? serverEntries : []);
      })
      .catch(() => clearToken()); // token invalid — stay on auth screen
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuth = useCallback((userData) => {
    setUser(userData);
    syncPlanFromServer(userData.plan); // sync plan from server on every login/register
    if (userData.isDemo) {
      startTrial('radiant', 14);
      setEntries(initialEntries); // demo mode: show mock entries
      return;
    }
    // Real user: clear entries immediately so demo data is never shown,
    // then replace with actual data from the server.
    setEntries([]);
    apiGetEntries()
      .then(serverEntries => setEntries(Array.isArray(serverEntries) ? serverEntries : []))
      .catch(() => setEntries([]));
  }, [startTrial, syncPlanFromServer]);

  const handleLogout = () => {
    clearToken();
    setUser(null);
    setTab('timeline');
    setShowLog(false);
    setShowPricing(false);
    setEntries([]);
  };

  const handleTabChange = (id) => {
    if (id === 'log') {
      if (!canLog(getThisMonthCount(entries))) {
        setShowPricing(true);
      } else {
        setShowLog(true);
      }
    } else {
      setShowLog(false);
      setTab(id);
    }
  };

  const handleSaveEntry = useCallback(async (entry) => {
    // Optimistically add to local state immediately so the UI feels instant
    setEntries(prev => [entry, ...prev].sort((a, b) => new Date(b.date) - new Date(a.date)));
    setShowLog(false);
    setTab('timeline');

    // Persist to backend (best-effort — local state already updated)
    if (getToken() && !user?.isDemo) {
      apiCreateEntry(entry).catch(() => {});
    }
  }, [user]);

  const renderTab = () => {
    switch (tab) {
      case 'timeline': return <TimelineView entries={entries} />;
      case 'routines': return <RoutinesView />;
      case 'wishlist': return <WishlistView onOpenPricing={() => setShowPricing(true)} />;
      case 'timelapse': return <TimelapseView entries={entries} onOpenPricing={() => setShowPricing(true)} />;
      case 'profile': return <ProfileView entries={entries} onLogout={handleLogout} onOpenPricing={() => setShowPricing(true)} />;
      case 'shop': return <ShopView />;
      default: return <TimelineView entries={entries} />;
    }
  };

  return (
    <div
      className="w-full relative overflow-hidden shadow-2xl flex flex-col bg-[#f8fafc]"
      style={{ height: '100dvh', maxWidth: '430px' }}
    >
      {/* Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-20%] w-[70%] h-[50%] rounded-full bg-[#d1ece5] blur-3xl opacity-60 mix-blend-multiply" />
        <div className="absolute top-[20%] right-[-20%] w-[60%] h-[60%] rounded-full bg-[#e6e0f8] blur-3xl opacity-60 mix-blend-multiply" />
        <div className="absolute bottom-[-10%] left-[10%] w-[80%] h-[50%] rounded-full bg-[#ffe0d2] blur-3xl opacity-50 mix-blend-multiply" />
      </div>

      <AnimatePresence mode="wait">
        {!user ? (
          <motion.div
            key="auth"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 z-50"
          >
            <AuthScreen onAuth={handleAuth} />
          </motion.div>
        ) : (
          <motion.div
            key="app"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            className="absolute inset-0 flex flex-col"
          >
            {/* Trial banner */}
            {isOnTrial && (
              <TrialBanner daysLeft={trialDaysLeft} onOpenPricing={() => setShowPricing(true)} />
            )}

            {/* Stripe checkout success banner */}
            {checkoutBanner === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex-shrink-0 z-20 relative"
                style={{ background: 'linear-gradient(90deg, #5C8A78, #7ecdc4)' }}
              >
                <button
                  className="w-full px-4 py-2 flex items-center justify-between gap-2"
                  onClick={() => setCheckoutBanner(null)}
                >
                  <span className="font-sans text-xs text-white font-medium">
                    ✓ Subscription activated — welcome to your new plan!
                  </span>
                  <span className="text-white/60 text-sm leading-none px-1">×</span>
                </button>
              </motion.div>
            )}

            {/* Main Tab Content */}
            <div className="flex-1 relative overflow-hidden min-h-0 z-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={tab}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 overflow-y-auto"
                >
                  {renderTab()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Bottom Nav */}
            <BottomNav current={tab} onChange={handleTabChange} />

            {/* Log Entry Overlay */}
            <AnimatePresence>
              {showLog && (
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                  className="absolute inset-0 z-50 bg-[#f8fafc]"
                >
                  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                    <div className="absolute top-[-10%] left-[-20%] w-[70%] h-[50%] rounded-full bg-[#d1ece5] blur-3xl opacity-60 mix-blend-multiply" />
                    <div className="absolute bottom-[-10%] right-[-20%] w-[80%] h-[50%] rounded-full bg-[#e6e0f8] blur-3xl opacity-60 mix-blend-multiply" />
                  </div>
                  <div className="relative z-10 h-full">
                    <LogView
                      onSave={handleSaveEntry}
                      onCancel={() => setShowLog(false)}
                      onOpenPricing={() => { setShowLog(false); setShowPricing(true); }}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pricing Overlay */}
            <AnimatePresence>
              {showPricing && (
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                  className="absolute inset-0 z-[60]"
                >
                  <PricingView onClose={() => setShowPricing(false)} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function App() {
  return (
    <SubscriptionProvider>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/app/*" element={<AppShell />} />
        <Route path="*" element={<AppShell />} />
      </Routes>
    </SubscriptionProvider>
  );
}
